/**
 * Editor de textos de Landing Page · Sales Data Room.
 *
 * Acessível apenas para usuários com role >= marketing (hierarchy_level 40+).
 *
 * Sprint 1 (atual): stub funcional — carrega o conteúdo bruto da LP via
 * GET /v1/sales-dataroom/lp-content/{slug} e exibe área de pré-visualização +
 * lista de textos editáveis. Salvamento parcial habilitado.
 *
 * Sprint 2 (próximo): parser semântico completo (h1/h2/h3/p/li/strong/em/span),
 * editor inline com diff visual, versionamento, restore.
 */
import { useEffect, useState } from 'react'
import { X, Loader2, ExternalLink, AlertTriangle, Save, Eye } from 'lucide-react'
import api from '@/lib/api'

type LpContent = {
  slug: string
  title: string | null
  description: string | null
  texts: Array<{ id: string; tag: string; text: string; selector?: string }>
  preview_url: string
}

export default function LandingPageEditorModal({ slug, onClose }: {
  slug: string
  onClose: () => void
}) {
  const [data, setData] = useState<LpContent | null>(null)
  const [edited, setEdited] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState<string | null>(null)

  useEffect(() => {
    let cancel = false
    setLoading(true)
    setError(null)
    api.get<LpContent>(`/v1/sales-dataroom/lp-content/${slug}`)
      .then(({ data }) => { if (!cancel) setData(data) })
      .catch((err) => {
        if (cancel) return
        setError(err?.response?.data?.detail || 'Erro ao carregar conteúdo da Landing Page')
      })
      .finally(() => { if (!cancel) setLoading(false) })
    return () => { cancel = true }
  }, [slug])

  async function handleSave() {
    if (!data || Object.keys(edited).length === 0) return
    setSaving(true)
    setSavedMsg(null)
    try {
      await api.put(`/v1/sales-dataroom/lp-content/${slug}`, { changes: edited })
      setSavedMsg(`${Object.keys(edited).length} alteração(ões) publicadas com sucesso`)
      // Recarrega depois de 1.2s
      setTimeout(() => {
        api.get<LpContent>(`/v1/sales-dataroom/lp-content/${slug}`)
          .then(({ data }) => { setData(data); setEdited({}) })
      }, 1200)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erro ao salvar')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase tracking-wider mb-2">
              Editor de Landing Page
            </div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {data?.title || slug}
            </h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 font-mono">
              /landingpages/{slug}/
            </p>
          </div>
          <button onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {loading && (
            <div className="flex items-center justify-center py-12 text-gray-500">
              <Loader2 className="h-6 w-6 animate-spin mr-2" />
              Carregando conteúdo…
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-4 text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
              <AlertTriangle className="h-5 w-5 flex-shrink-0" />
              <div>
                <p className="font-bold">Não foi possível abrir o editor</p>
                <p className="mt-1">{error}</p>
                <p className="mt-2 text-xs text-red-600 dark:text-red-400">
                  Endpoint backend ainda em construção. Use <a href={`https://social.myncc.com.br/landingpages/${slug}/`} target="_blank" rel="noopener noreferrer" className="underline">o link direto</a> para visualizar.
                </p>
              </div>
            </div>
          )}

          {!loading && !error && data && (
            <>
              {savedMsg && (
                <div className="mb-4 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-700 dark:text-emerald-300">
                  ✓ {savedMsg}
                </div>
              )}

              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                {data.texts.length} elementos textuais detectados.
                Edite e clique em <strong>Salvar</strong> para publicar no ar.
              </p>

              <div className="space-y-3">
                {data.texts.map((t) => {
                  const value = edited[t.id] ?? t.text
                  const changed = edited[t.id] !== undefined && edited[t.id] !== t.text
                  return (
                    <div key={t.id}
                      className={`rounded-lg border p-3 transition ${
                        changed
                          ? 'border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-900/10'
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/40'
                      }`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[10px] font-mono font-bold uppercase text-gray-500 dark:text-gray-400">
                          {t.tag}
                        </span>
                        {changed && (
                          <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300">modificado</span>
                        )}
                      </div>
                      <textarea
                        value={value}
                        onChange={(e) => setEdited({ ...edited, [t.id]: e.target.value })}
                        rows={Math.max(1, Math.ceil(value.length / 80))}
                        className="w-full px-2 py-1.5 rounded bg-transparent border border-transparent hover:border-gray-300 focus:border-blue-500 dark:hover:border-gray-600 dark:focus:border-blue-400 outline-none text-sm text-gray-900 dark:text-gray-100 resize-vertical font-sans"
                      />
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
          <a href={data?.preview_url || `https://social.myncc.com.br/landingpages/${slug}/`}
            target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:underline">
            <Eye className="h-3.5 w-3.5" />
            Pré-visualizar
            <ExternalLink className="h-3 w-3" />
          </a>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 text-sm font-semibold hover:bg-gray-100 dark:hover:bg-gray-800">
              Cancelar
            </button>
            <button type="button" onClick={handleSave}
              disabled={saving || Object.keys(edited).length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              {saving ? 'Salvando…' : 'Salvar alterações'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
